import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { PlanLimitError } from '../../features/plans/models/enforcement.model';
import { PlanLimitStateService } from '../../features/plans/services/plan-limit-state.service';
import { ExpertRole } from '../../models/expert-role.model';
import { SessionInfoDto } from '../../models/chat/chat-session.models';
import { ChatSource, CsvDocumentInfo } from '../../models/chat.model';
import { ChatInputMode, ChatItem, ChatQueryUiResult } from '../../models/chat-ui.model';
import { ChatService } from '../../services/chat/api/chat.service';
import { CsvTaskPollingService } from '../../services/chat/api/csv-task-polling.service';
import { ChatCsvFlowService, ChatCsvSelection, ChatCsvSubmitEvent } from '../../services/chat/flow/chat-csv-flow.service';
import { ChatMessageFlowEvent, ChatMessageFlowService } from '../../services/chat/flow/chat-message-flow.service';
import { ChatSourceMapperService } from '../../services/chat/mapping/chat-source-mapper.service';
import { AnswerSourceDocument, ChatAnswerSourcesService, ParsedSourceSummary } from '../../services/chat/presentation/chat-answer-sources.service';
import { ChatActionBuilderService } from '../../services/chat/presentation/chat-action-builder.service';
import { ChatCursorHintService } from '../../services/chat/presentation/chat-cursor-hint.service';
import { ChatDomEffectsService } from '../../services/chat/presentation/chat-dom-effects.service';
import { ChatFeedbackPanelState, ChatFeedbackUiService } from '../../services/chat/presentation/chat-feedback-ui.service';
import { ChatIdService } from '../../services/chat/presentation/chat-id.service';
import { ChatMessageTextService } from '../../services/chat/presentation/chat-message-text.service';
import { ChatSessionLoaderService } from '../../services/chat/session/chat-session-loader.service';
import { ChatSessionUiService } from '../../services/chat/session/chat-session-ui.service';
import { ChatStorageService } from '../../services/chat/session/chat-storage.service';
import { ExpertRolesService } from '../../services/expert-roles/expert-roles.service';
import { FrontendLoggerService } from '../../services/frontend-logger.service';
import { GenerationService } from '../../services/generation/generation.service';
import { LanguageService } from '../../services/language/language.service';
import { NotificationService } from '../../services/notification.service';
import { TaskNavigationService } from '../../services/task-navigation.service';
import { UserService } from '../../services/user.service';
import { ChatConversationMetadata } from './chat-runtime.models';
import { ChatRuntimeController } from './chat-runtime.controller';

export type { ChatConversationMetadata } from './chat-runtime.models';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: [
    './chat.component.toolbar-and-shell.scss',
    './chat.component.message-interactions.scss',
    './chat.component.answer-content-and-feedback.scss',
    './chat.component.composer-and-details.scss',
    './chat.component.source-list.scss',
    './chat.component.themes-and-source-detail.scss',
    './chat.component.grounding.scss',
    './chat.component.context-menu-and-role-selector.scss',
    './chat.component.role-feedback.scss',
    './chat-modal.component.dialog-layout.scss',
    './chat-modal.component.session-editor.scss',
  ],
  standalone: false,
})
export class ChatComponent implements OnInit, OnDestroy {
  readonly runtime: ChatRuntimeController;

  @ViewChild('cursorHint')
  set cursorHintReference(value: ElementRef<HTMLElement> | undefined) {
    this.runtime.state.cursorHintRef = value;
  }

  get cursorHintRef(): ElementRef<HTMLElement> | undefined {
    return this.runtime.state.cursorHintRef;
  }

  set cursorHintRef(value: ElementRef<HTMLElement> | undefined) {
    this.runtime.state.cursorHintRef = value;
  }

  constructor(
    formBuilder: FormBuilder,
    chatService: ChatService,
    language: LanguageService,
    csvPolling: CsvTaskPollingService,
    logger: FrontendLoggerService,
    sessionUi: ChatSessionUiService,
    changeDetector: ChangeDetectorRef,
    actionBuilder: ChatActionBuilderService,
    messageText: ChatMessageTextService,
    sourceMapper: ChatSourceMapperService,
    storage: ChatStorageService,
    dom: ChatDomEffectsService,
    sessionLoader: ChatSessionLoaderService,
    private readonly chatMessageFlow: ChatMessageFlowService,
    csvFlow: ChatCsvFlowService,
    feedback: ChatFeedbackUiService,
    ids: ChatIdService,
    router: Router,
    taskNavigation: TaskNavigationService,
    userService: UserService,
    planLimits: PlanLimitStateService,
    private readonly notificationService: NotificationService,
    answerSources: ChatAnswerSourcesService,
    cursorHint: ChatCursorHintService,
    expertRoles: ExpertRolesService,
    generation: GenerationService
  ) {
    this.runtime = new ChatRuntimeController({
      formBuilder, chatService, language, csvPolling, logger, sessionUi,
      changeDetector, actionBuilder, messageText, sourceMapper, storage, dom,
      sessionLoader, messageFlow: chatMessageFlow, csvFlow, feedback, ids,
      router, taskNavigation, userService, planLimits,
      notifications: notificationService, answerSources, cursorHint,
      expertRoles, generation,
    });
  }

  get chatForm(): FormGroup { return this.runtime.state.chatForm; }
  set chatForm(value: FormGroup) { this.runtime.state.chatForm = value; }
  get messages(): ChatItem[] { return this.runtime.state.messages; }
  set messages(value: ChatItem[]) { this.runtime.state.messages = value; }
  get expertRoles(): ExpertRole[] { return this.runtime.state.expertRoles; }
  set expertRoles(value: ExpertRole[]) { this.runtime.state.expertRoles = value; }
  get selectedExpertRole(): string | null { return this.runtime.state.selectedExpertRole; }
  set selectedExpertRole(value: string | null) { this.runtime.state.selectedExpertRole = value; }
  get lastAppliedExpertRole(): string | null { return this.runtime.state.lastAppliedExpertRole; }
  set lastAppliedExpertRole(value: string | null) { this.runtime.state.lastAppliedExpertRole = value; }
  get selectedSource(): ChatSource | null { return this.runtime.state.selectedSource; }
  set selectedSource(value: ChatSource | null) { this.runtime.state.selectedSource = value; }
  get planLimitError(): PlanLimitError | null { return this.runtime.state.planLimitError; }
  set planLimitError(value: PlanLimitError | null) { this.runtime.state.planLimitError = value; }
  get selectedSourceSummary(): ParsedSourceSummary | null { return this.runtime.selectedSourceSummary; }
  get isExpertRoleSelectorVisible(): boolean { return this.runtime.isExpertRoleSelectorVisible; }

  ngOnInit(): void { this.runtime.initialize(); }
  ngOnDestroy(): void { this.runtime.destroy(); }
  translate(key: string): string { return this.runtime.translate(key); }
  normalizeMessageText(html: string): string { return this.runtime.normalizeMessageText(html); }
  displayMessageContent(message: ChatItem): string { return this.runtime.displayMessageContent(message); }
  shouldRenderMessageAsHtml(message: ChatItem): boolean { return this.runtime.shouldRenderMessageAsHtml(message); }
  openTaskAction(id: string, event?: Event): void { this.runtime.openTaskAction(id, event); }
  openRouteAction(action: any, event?: Event): void { this.runtime.openRouteAction(action, event); }
  openGenerateAction(action: any, event?: Event): void { this.runtime.openGenerateAction(action, event); }
  onChatClicked(event: MouseEvent): void { this.runtime.onChatClicked(event); }
  editMessageToInput(message: { content: string }): void { this.runtime.editMessageToInput(message); }
  copyMessage(message: Pick<ChatItem, 'content'>, index: number): Promise<void> { return this.runtime.copyMessage(message, index); }
  toggleFeedbackForMessage(index: number, like: boolean): void { this.runtime.toggleFeedbackForMessage(index, like); }
  closeInlineFeedback(): void { this.runtime.closeInlineFeedback(); }
  onBotMessageHintEnter(message: ChatItem | null | undefined, event: MouseEvent): void { this.runtime.onBotMessageHintEnter(message, event); }
  onBotMessageHintLeave(): void { this.runtime.onBotMessageHintLeave(); }
  selectMessage(message: ChatItem): void { this.runtime.selectMessage(message); }
  isMessageSelected(message: ChatItem | null | undefined): boolean { return this.runtime.isMessageSelected(message); }
  deselectMessage(event?: MouseEvent): void { this.runtime.deselectMessage(event); }
  get isLastExpertRoleDegraded(): boolean { return this.runtime.isLastExpertRoleDegraded; }
  get selectedExpertRoleDisplayName(): string | null { return this.runtime.selectedExpertRoleDisplayName; }
  get selectedExpertRoleIcon(): string { return this.runtime.selectedExpertRoleIcon; }
  get defaultExpertRole(): string | null { return this.runtime.defaultExpertRole; }
  get isCurrentModeBlocked(): boolean { return this.runtime.isCurrentModeBlocked; }
  closeRoleDropdown(): void { this.runtime.closeRoleDropdown(); }
  onRoleOptionSelected(code: string | null): void { this.runtime.onRoleOptionSelected(code); }
  @HostListener('document:keydown.escape') onEscapeCloseRoleDropdown(): void { this.runtime.onEscapeCloseRoleDropdown(); }
  selectExpertRole(code: string | null): void { this.runtime.selectExpertRole(code); }
  roleIcon(role: ExpertRole | null | undefined): string { return this.runtime.roleIcon(role); }
  hasExpertRoleBadge(message: ChatItem): boolean { return this.runtime.hasExpertRoleBadge(message); }
  expertRoleBadge(slug: string): { icon: string; name: string } { return this.runtime.expertRoleBadge(slug); }
  capitalizeSlug(slug: string): string { return this.runtime.capitalizeSlug(slug); }
  loadExpertRoles(): void { this.runtime.loadExpertRoles(); }
  restoreSelectedExpertRole(): void { this.runtime.restoreSelectedExpertRole(); }
  clearSelectedExpertRole(): void { this.runtime.clearSelectedExpertRole(); }
  autoGrowTextarea(target: EventTarget | null): void { this.runtime.autoGrowTextarea(target); }
  onChatKeydown(event: KeyboardEvent): void { this.runtime.onChatKeydown(event); }
  submitChat(override?: string): void { this.runtime.submitChat(override); }
  generateSummary(): void { this.runtime.generateSummary(); }
  cancelGenerateSummary(): void { this.runtime.cancelGenerateSummary(); }
  selectInputMode(mode: ChatInputMode): void { this.runtime.selectInputMode(mode); }
  toggleToolsPanel(): void { this.runtime.toggleToolsPanel(); }
  getStarColor(star: number): string { return this.runtime.getStarColor(star); }
  getStarClass(star: number): string { return this.runtime.getStarClass(star); }
  setHoverRating(value: number): void { this.runtime.setHoverRating(value); }
  setFeedbackRating(value: number): void { this.runtime.setFeedbackRating(value); }
  setTopK(value: number): void { this.runtime.setTopK(value); }
  generateMessageEntityId(): string { return this.runtime.generateMessageEntityId(); }
  startRequest(): void { this.runtime.startRequest(); }
  endRequest(): void { this.runtime.endRequest(); }
  endRequestIfStuck(): void { this.runtime.endRequestIfStuck(); }
  sendMessage(override?: string): void { this.runtime.sendMessage(override); }
  applyMessageFlowEvent(event: ChatMessageFlowEvent): void { this.runtime.applyMessageFlowEvent(event); }
  applyQueryUiResult(result: ChatQueryUiResult): void { this.runtime.applyQueryUiResult(result); }
  parseConversationMetadata(value: unknown): ChatConversationMetadata { return this.runtime.parseConversationMetadata(value); }
  getSelectedChunks(): void { this.runtime.getSelectedChunks(); }
  toggleDetails(): void { this.runtime.toggleDetails(); }
  selectSource(source: ChatSource): void { this.runtime.selectSource(source); }
  parseSourceSummary(source: ChatSource | null | undefined): ParsedSourceSummary | null { return this.runtime.parseSourceSummary(source); }
  getAnswerSourceDocuments(message: ChatItem | null | undefined): AnswerSourceDocument[] { return this.runtime.getAnswerSourceDocuments(message); }
  downloadAnswerDocument(id: string, event?: MouseEvent): void { this.runtime.downloadAnswerDocument(id, event); }
  onSourceDownloadClick(event: MouseEvent, source: ChatSource | null): void { this.runtime.onSourceDownloadClick(event, source); }
  getSourceDownloadUrl(source: ChatSource | null): string | null { return this.runtime.getSourceDownloadUrl(source); }
  onSourceRightClick(event: MouseEvent, source: ChatSource): void { this.runtime.onSourceRightClick(event, source); }
  trackBySource(index: number, source: ChatSource): string | number { return this.runtime.trackBySource(index, source); }
  clearSource(): void { this.runtime.clearSource(); }
  sendFeedback(liked: boolean | null): void { this.runtime.sendFeedback(liked); }
  applyFeedbackPanelState(state: ChatFeedbackPanelState): void { this.runtime.applyFeedbackPanelState(state); }
  scrollToBottom(): void { this.runtime.scrollToBottom(); }
  onCsvCancel(): void { this.runtime.onCsvCancel(); }
  onCsvSubmit(data: ChatCsvSelection): void { this.runtime.onCsvSubmit(data); }
  applyCsvSubmitEvent(event: ChatCsvSubmitEvent): void { this.runtime.applyCsvSubmitEvent(event); }
  openContextModalForSource(source: ChatSource, event?: MouseEvent): void { this.runtime.openContextModalForSource(source, event); }
  beginContextQuestion(): void { this.runtime.beginContextQuestion(); }
  confirmContextSelection(value: boolean): void { this.runtime.confirmContextSelection(value); }
  applyInitialSessionLoadResult(result: any): void { this.runtime.applyInitialSessionLoadResult(result); }
  applySelectedSessionLoadResult(result: any): void { this.runtime.applySelectedSessionLoadResult(result); }
  clearChatHistory(): void { this.runtime.clearChatHistory(); }
  onSessionSelected(id: string): void { this.runtime.onSessionSelected(id); }
  onSessionRecover(): void { this.runtime.onSessionRecover(); }
  onNewSession(): void { this.runtime.onNewSession(); }
  onCloseEvent(): void { this.runtime.onCloseEvent(); }
  onClearLimitedDoc(): void { this.runtime.onClearLimitedDoc(); }
  dismissPlanLimit(): void { this.runtime.dismissPlanLimit(); }
  showPlanLimit(error: PlanLimitError): void { this.runtime.showPlanLimit(error); }
  updateButtonText(text: string): void { this.runtime.updateButtonText(text); }
  changeName(session: SessionInfoDto): void { this.runtime.changeName(session); }
  saveSessionName(session: SessionInfoDto): void { this.runtime.saveSessionName(session); }
  cancelEditSessionName(): void { this.runtime.cancelEditSessionName(); }
  deriveSessionTitle(text: string): string { return this.runtime.deriveSessionTitle(text); }
  maybeAutoTitleSession(id: string | null): void { this.runtime.maybeAutoTitleSession(id); }
}
